import React, { useMemo, useState } from 'react';
import type { ProposalView } from '../utils/nltWorkflowProposals';
import { buildSplitDiffRows, buildTextDiffLines, diffStats, toUnifiedOrder } from '../utils/textFieldDiff';
import WorkflowModal from './connector/workflow/WorkflowModal';
import './NltProposalDiffModal.css';

/**
 * Полноэкранный просмотр одного предложения NLT (AIT-297).
 *
 * Панель NLT узкая — в ней помещается только короткая сводка, и на длинном
 * промпте или объекте разобрать правку в ней нельзя. Здесь та же правка во всю
 * ширину: два режима, номера строк с обеих сторон и адрес, разложенный на части.
 */

export interface NltProposalDiffModalProps {
  view: ProposalView | null;
  onClose: () => void;
}

type Mode = 'split' | 'unified';

const Breadcrumb: React.FC<{ view: ProposalView }> = ({ view }) => (
  <span className="npd__where">
    {view.where.scope !== 'unknown' ? (
      <span className={`npd__scope npd__scope--${view.where.scope}`}>{view.where.scope}</span>
    ) : null}
    {view.where.owner ? <span className="npd__owner">{view.where.owner}</span> : null}
    {view.where.path.slice(0, -1).map((part, i) => (
      <span key={`${part}-${i}`} className="npd__crumb">
        {part}
      </span>
    ))}
    <span className="npd__field">{view.where.field}</span>
  </span>
);

const NltProposalDiffModal: React.FC<NltProposalDiffModalProps> = ({ view, onClose }) => {
  const [mode, setMode] = useState<Mode>('split');

  const lines = useMemo(
    () => (view && view.hasValueDiff ? buildTextDiffLines(view.oldValue, view.newValue) : []),
    [view]
  );
  const rows = useMemo(
    () => (view && view.hasValueDiff ? buildSplitDiffRows(view.oldValue, view.newValue) : []),
    [view]
  );
  const stats = useMemo(() => diffStats(lines), [lines]);
  // В едином режиме принято показывать сперва то, что было.
  const unified = useMemo(() => toUnifiedOrder(lines), [lines]);

  if (!view) return null;
  const { proposal } = view;

  return (
    <WorkflowModal
      open
      xwide
      icon="fa-code-compare"
      onClose={onClose}
      title={
        <span className="npd__title">
          <Breadcrumb view={view} />
        </span>
      }
      footer={
        <>
          <span className="npd__address" title={proposal.address}>
            {proposal.address}
          </span>
          <button type="button" className="cwf-btn" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <div className="npd">
        <div className="npd__bar">
          <span className={`npd__badge npd__badge--${view.outcome}`}>{view.outcomeLabel}</span>
          {proposal.operation?.op ? <span className="npd__op">{proposal.operation.op}</span> : null}
          {view.hasValueDiff ? (
            <span className="npd__stats">
              <span className="npd__stat npd__stat--add">+{stats.added}</span>
              <span className="npd__stat npd__stat--del">−{stats.removed}</span>
            </span>
          ) : null}
          {view.hasValueDiff ? (
            <span className="npd__modes" role="group" aria-label="Diff layout">
              {(['split', 'unified'] as Mode[]).map((m) => (
                <button
                  key={m}
                  type="button"
                  className={`npd__mode${mode === m ? ' npd__mode--on' : ''}`}
                  aria-pressed={mode === m}
                  onClick={() => setMode(m)}
                >
                  {m === 'split' ? 'Side by side' : 'Unified'}
                </button>
              ))}
            </span>
          ) : null}
        </div>

        {!view.writable ? (
          <p className="npd__note npd__note--warn">
            <i className="fas fa-lock" aria-hidden="true" /> Global section — this connector only accepts writes to{' '}
            <code>workflow</code>, so this one cannot be applied from here.
          </p>
        ) : null}
        {view.reason ? <p className="npd__note">{view.reason}</p> : null}

        {!view.resolved ? (
          <p className="npd__note npd__note--warn">
            Address <code>{proposal.address}</code> was not found in the workflow that was sent, so there is nothing to
            compare — most likely the graph moved between reading and this turn.
          </p>
        ) : !view.hasValueDiff ? (
          <>
            <div className="npd__label">value at this address</div>
            <pre className="npd__value">{view.oldValue || '—'}</pre>
            {view.outcome === 'applied' ? (
              <p className="npd__note">The operation landed without altering the value.</p>
            ) : null}
          </>
        ) : mode === 'split' ? (
          <div className="npd__split" role="table">
            <div className="npd__split-head" role="row">
              <span role="columnheader">before</span>
              <span role="columnheader">after</span>
            </div>
            <div className="npd__split-body">
              {rows.map((row, i) => (
                <div key={i} className={`npd__row npd__row--${row.kind}`} role="row">
                  <span className="npd__no">{row.leftNo ?? ''}</span>
                  <span className="npd__side npd__side--left">{row.left ?? ''}</span>
                  <span className="npd__no">{row.rightNo ?? ''}</span>
                  <span className="npd__side npd__side--right">{row.right ?? ''}</span>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="npd__unified">
            {unified.map((line, i) => (
              <div key={i} className={`npd__uline npd__uline--${line.kind}`}>
                <span className="npd__gutter">{line.kind === 'added' ? '+' : line.kind === 'removed' ? '−' : ' '}</span>
                <span className="npd__utext">{line.text}</span>
              </div>
            ))}
          </div>
        )}

        {proposal.operation?.value !== undefined ? (
          <details className="npd__raw">
            <summary>proposed operation</summary>
            <pre className="npd__value">
              {typeof proposal.operation.value === 'string'
                ? proposal.operation.value
                : JSON.stringify(proposal.operation.value, null, 2)}
            </pre>
          </details>
        ) : null}
      </div>
    </WorkflowModal>
  );
};

export default NltProposalDiffModal;
